<template>
  <v-container fluid class="grey700 px-8 py-6">
    <h5 class="text-h6 text-uppercase font-weight-bold primary500--text">
      Withdraw items
    </h5>
    <p class="text-body-2 mb-1">
      Please confirm your withdrawal while we check if these items are in stock.
    </p>

    <!-- withdraw limit -->
    <p class="text-caption text--secondary">
      <i18n path="page.shop.daily_limit" class="mr-1">
        <span :class="[itemValue > withdrawableAmount ? 'primary500--text' : 'blue200--text']">
          ${{ withdrawableAmount | toScrap }}
        </span>
      </i18n>
      <a
        href="https://banditcamp.tawk.help/article/why-can-i-not-withdraw-more-from-the-shop"
        target="_blank" rel="noopener noreferrer"
        class="link blue200--text"
      >
        <fai :icon="['fas', 'question-circle']" />
      </a>
    </p>

    <!-- error -->
    <v-alert v-if="error" tile dense color="primary700 primary100--text mb-4">
      <div class="d-flex align-center my-1">
        <fai :icon="['fad', 'siren-on']" class="text-h5" />
        <p class="mb-0 mx-4 text-caption lh-1-4" v-text="error" />
      </div>
    </v-alert>

    <!-- items / stock -->
    <div v-if="items.length" class="items mb-3">
      <div
        v-for="(item, i) in items" :key="item.id"
        class="cart-item bg-ui-panel d-flex align-center mb-1 pa-3"
      >
        <div class="item-info flex-grow-1 d-flex align-center">
          <div class="bg-ui-panel pa-1 flex-grow-0 mr-3">
            <v-img
              :src="`https://community.cloudflare.steamstatic.com/economy/image/${item.image}/164x164`"
              aspect-ratio="1" width="40"
            />
          </div>
          <div class="grey100--text ">
            <div class="text-body-2 lh-1-3">
              {{ item.name }}
            </div>

            <div v-if="!item.inStock">
              <span v-if="loading.stock" class="text-caption text--disabled">
                <fai :icon="['fad', 'sync']" spin class="mr-1" />
                Checking stock...
              </span>
              <a
                v-else
                href="https://banditcamp.tawk.help/article/item-not-in-stock"
                target="_blank" rel="noopener noreferrer"
                class="link font-weight-bold lh-1 text-caption primary400--text"
              >
                Not available
                <fai :icon="['fas', 'question-circle']" />
              </a>
            </div>

            <div v-else class="text--secondary lh-1-3 font-weight-semibold text-caption">
              ${{ item.price | toScrap }}
            </div>
          </div>
        </div>
        <v-btn
          v-if="item.inStock === false"
          small :ripple="false" tile depressed
          color="primary700 primary200--text"
          :loading="loading.stock"
          :disabled="loading.stock"
          @click="onItemRemove(i)"
        >
          Remove
        </v-btn>
        <div v-else-if="item.inStock" class="px-2 text-no-wrap text-right text-body-2">
          <fai :icon="['fas', 'check']" class="green400--text mr-1" />
          In stock
        </div>
      </div>
    </div>

    <!-- express delivery info -->
    <div
      v-if="outOfStockItems.length"
      class="grey500 text-caption py-2 px-3 d-flex justify-center align-center mb-2"
    >
      <fai :icon="['fad', 'info-circle']" class="blue300--text text-body-2 mr-2" />
      <span class="font-weight-semibold">Swap out of stock items instead of selling.</span>
    </div>

    <v-btn
      v-if="!user.tradeUrl"
      v-t="'page.shop.problem.tradeUrl'"
      large tile block depressed
      color="blue800 blue500--text" :ripple="false"
      class="font-weight-semibold"
      @click="$modal.open('account', { maxWidth: 1000 }, {tab: 'settings'})"
    />

    <v-btn
      v-else
      depressed block large tile
      color="green800 green300--text"
      class="font-weight-semibold"
      :ripple="false"
      :loading="loading.stock || loading.withdraw"
      :disabled="!canWithdraw"
      @click="onWithdraw"
    >
      <span v-if="!canWithdraw">
        <span v-if="items.some(i => !i.inStock)">Remove out of stock items</span>
        <span v-else-if="itemValue > withdrawableAmount">Over withdraw limit</span>
        <span v-else-if="itemValue < minAmount">Minimum ${{ minAmount | toScrap }}</span>
        <span v-else-if="items.length < 1">No items to withdraw</span>
        <span v-else-if="items.length > maxItems">Max {{ maxItems }} items</span>
      </span>
      <span v-else>Withdraw {{ items.length }} items</span>
    </v-btn>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  props: {
    close: {
      type: Function,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      error: null,
      loading: {
        stock: false,
        withdraw: false,
      },
      withdrawableAmount: 0,
      // remove store binding
      items: this.payload?.items.map(i => i.$toJson()),
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/session',
      conga: 'conga/conga',
    }),
    minAmount() {
      return this.conga.withdrawals.market?.minAmount ?? 1
    },
    maxItems() {
      return this.conga.withdrawals.market?.maxItems ?? 50
    },
    outOfStockItems() {
      return this.items.filter(i => !i.inStock)
    },
    itemValue() {
      return this.items.reduce((acc, v) => acc + v.price, 0)
    },
    canWithdraw() {
      if (this.items.some(i => !i.inStock)) return false
      if (this.items.length < 1) return false
      if (this.items.length > this.maxItems) return false
      if (this.items.length > 50) return false
      if (!this.user.tradeUrl) return false
      if (this.itemValue > this.withdrawableAmount) return false
      if (this.itemValue < this.minAmount) return false

      return true
    },
  },
  created() {
    this.onCheckStock(this.items.map(i => i.id))
    this.getWithdrawable()
  },
  methods: {
    ...mapActions({
      _getWithdrawable: 'user/getWithdrawable',
      checkItemStock: 'user/checkItemStock',
      withdrawItems: 'user/withdrawItems',
    }),
    ...mapMutations({
      setItemsStockInfo: 'user/setItemsStockInfo',
    }),
    async onCheckStock(ids) {
      if (this.loading.stock) return
      this.loading.stock = true
      this.error = null

      try {
        const stockInfo = await this.checkItemStock(ids)

        const { items } = this
        for (const { id, inStock } of stockInfo) {
          const index = items.findIndex(i => i.id === id)

          if (index !== -1) {
            items[index].inStock = inStock
          }
        }

        this.setItemsStockInfo(stockInfo)
      } catch (error) {
        this.error = this.$i18n.t(`errors.${error?.message ?? 'UnknownError'}`)
      } finally {
        this.loading.stock = false
      }
    },
    async getWithdrawable() {
      try {
        this.withdrawableAmount = await this._getWithdrawable()
      } catch (error) {
        this.error = this.$i18n.t(`errors.${error?.message ?? 'UnknownError'}`)
      }
    },
    async onWithdraw() {
      if (!this.canWithdraw) return
      if (this.loading.withdraw) return

      this.error = null
      this.loading.withdraw = true

      try {
        await this.withdrawItems(this.items.map(i => i.id))

        this.$toast.success('Your trade should arrive within 2-5 minutes.')
        this.$modal.open('account', { maxWidth: 1000 }, { tab: 'transactions' })
        this.close()
      } catch (error) {
        this.error = this.$i18n.t(`errors.${error?.message ?? 'UnknownError'}`)
      } finally {
        this.loading.withdraw = false
      }
    },
    //
    onItemRemove(index) {
      this.items.splice(index, 1)
    },
  },
}

</script>

<style lang="scss" scoped>
.items {
  overflow-y: auto;
  max-height: 400px;
}
</style>
